import React from "react";
import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { RagBand } from "./ragBand";

export type MarkerBand = Exclude<RagBand, "none"> | "loading";

// ui-theme base colours (strongRed, darkTangerine, treeGreen, moodysBlueColor).
export const MarkerColours = {
  red: "#cb1727",
  amber: "#f1a71c",
  amberEdge: "#a86b00",
  green: "#0f7c10",
  resolving: "#0028a0",
  resolvingOnDark: "#9fbcff",
  loading: "#cfd8dc",
};

const pulse = keyframes`
  0%, 100% { opacity: 0.25; }
  50% { opacity: 0.8; }
`;

const Svg = styled.svg<{ pulsing: boolean }>`
  display: block;
  flex: none;
  ${({ pulsing }) => (pulsing ? `animation: ${pulse} 1.2s ease-in-out infinite;` : "")}
`;

type Props = {
  band: MarkerBand;
  size?: number;
  /** Accessible name; omit for a decorative copy (e.g. next to text that says the same). */
  label?: string;
  /** Inside the dark tooltip the stopwatch is drawn light. */
  onDark?: boolean;
  testId?: string;
};

const Shape = ({ band, onDark }: { band: MarkerBand; onDark: boolean }) => {
  switch (band) {
    case "red":
      return (
        <>
          <path d="M8 1.3 L15.2 14.6 H0.8 Z" fill={MarkerColours.red} stroke={MarkerColours.red} strokeWidth="1.2" strokeLinejoin="round" />
          <path d="M8 5.6 V10" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" />
          <circle cx="8" cy="12.4" r="1.05" fill="#fff" />
        </>
      );
    case "amber":
      return (
        <>
          <path d="M8 1.6 L15 14.4 H1 Z" fill="#fff" />
          <polygon points="4.5,8 11.5,8 15,14.4 1,14.4" fill={MarkerColours.amber} />
          <path d="M8 1.6 L15 14.4 H1 Z" fill="none" stroke={MarkerColours.amberEdge} strokeWidth="1.4" strokeLinejoin="round" />
        </>
      );
    case "green":
      return (
        <>
          <path d="M8 1.3 L15.2 14.6 H0.8 Z" fill={MarkerColours.green} stroke={MarkerColours.green} strokeWidth="1.2" strokeLinejoin="round" />
          <path d="M5.1 10.3 L7.2 12.3 L10.9 7.4" fill="none" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </>
      );
    case "resolving": {
      const colour = onDark ? MarkerColours.resolvingOnDark : MarkerColours.resolving;
      const hand = onDark ? "#2e2e43" : "#fff";
      return (
        <>
          <rect x="6.3" y="0.6" width="3.4" height="1.8" rx="0.6" fill={colour} />
          <path d="M8 2.4 V3.4" stroke={colour} strokeWidth="1.4" />
          <path d="M12.6 3.6 L13.7 4.7" stroke={colour} strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="8" cy="9.3" r="5.9" fill={colour} />
          <path d="M8 9.3 V5.6 M8 9.3 L10.4 10.7" fill="none" stroke={hand} strokeWidth="1.5" strokeLinecap="round" />
        </>
      );
    }
    case "loading":
      return <path d="M8 1.5 L15 14.5 H1 Z" fill={MarkerColours.loading} />;
  }
};

/**
 * Progress marker. Shape and fill encode the band as well as colour: red is solid with "!",
 * amber is an outline with the lower half filled, green is solid with a tick, resolving is a
 * neutral-blue stopwatch and loading is a pale pulse.
 */
export const RagMarker = ({ band, size = 17, label, onDark = false, testId }: Props): JSX.Element => (
  <Svg
    viewBox="0 0 16 16"
    width={size}
    height={size}
    pulsing={band === "loading"}
    data-testid={testId}
    data-rag={band}
    {...(label ? { role: "img", "aria-label": label } : { "aria-hidden": true })}
  >
    <Shape band={band} onDark={onDark} />
  </Svg>
);
